import { Injectable } from "@angular/core";

// WebUSB transport for an 80mm ESC/POS thermal printer.
// Pairing persists per origin via Chrome's WebUSB device permissions, so
// once Jeff selects the printer in the picker, future visits resolve via
// navigator.usb.getDevices() without re-prompting.

const USB_PRINTER_CLASS = 7;

@Injectable()
export class ThermalPrinterService {
  private device: any = null;

  isSupported(): boolean {
    return typeof navigator !== "undefined" && !!(navigator as any).usb;
  }

  async getPairedDevice(): Promise<any | null> {
    if (this.device) return this.device;
    if (!this.isSupported()) return null;
    const devices = await (navigator as any).usb.getDevices();
    this.device = devices[0] || null;
    return this.device;
  }

  // Must be called from within a user-gesture handler (click / keypress).
  async pair(): Promise<any> {
    if (!this.isSupported()) {
      throw new Error("WebUSB is not supported in this browser.");
    }
    this.device = await (navigator as any).usb.requestDevice({
      filters: [{ classCode: USB_PRINTER_CLASS }],
    });
    return this.device;
  }

  unpair(): void {
    this.device = null;
  }

  async print(bytes: Uint8Array): Promise<void> {
    const device = await this.getPairedDevice();
    if (!device) {
      throw new Error("No printer paired.");
    }

    let claimedInterface: number | null = null;
    let opened = false;
    try {
      if (!device.opened) {
        await device.open();
        opened = true;
      }
      if (device.configuration === null) {
        await device.selectConfiguration(1);
      }

      const { interfaceNumber, endpointNumber } = findOutEndpoint(device);
      await device.claimInterface(interfaceNumber);
      claimedInterface = interfaceNumber;

      await device.transferOut(endpointNumber, bytes);
    } finally {
      if (claimedInterface !== null) {
        try { await device.releaseInterface(claimedInterface); } catch {}
      }
      if (opened) {
        try { await device.close(); } catch {}
      }
    }
  }
}

function findOutEndpoint(device: any): { interfaceNumber: number; endpointNumber: number } {
  const config = device.configuration;
  if (!config) throw new Error("Printer has no active configuration.");
  for (const iface of config.interfaces) {
    for (const alt of iface.alternates) {
      for (const ep of alt.endpoints) {
        if (ep.direction === "out" && ep.type === "bulk") {
          return { interfaceNumber: iface.interfaceNumber, endpointNumber: ep.endpointNumber };
        }
      }
    }
  }
  throw new Error("Printer has no bulk OUT endpoint.");
}
